
import React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import SparklesIcon from './icons/SparklesIcon';
import type { Opportunity, UserRole } from '../types';
import { ROLE_TYPE_MAP, isPast } from '../utils/recommendationUtils';

const normalize = (val: string) => (val || '').toLowerCase().split(',')[0].trim();

export const AroundMeSection: React.FC = () => {
  const { currentUser, managedProfile } = useAuth();
  const { opportunities } = useOpportunities();
  const { handleViewOpportunity, handleNavigation } = useNavigation();

  const recommendedOpportunities = React.useMemo(() => {
    if (!currentUser) return [];

    const activeTier = managedProfile?.subscriptionTier || currentUser?.subscriptionTier;
    const isFan = activeTier === 'fan_basic';
    const fanAllowedTypes: Opportunity['type'][] = ['Gig', 'Event', 'Jam Session', 'Live Show', 'Stage Play', 'Open Mic', 'DJ set', 'Stand Up Comedy', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'];

    const userInstruments = (currentUser.instruments || []).map(i => i.toLowerCase());
    const userGenres = (currentUser.genres || []).map(g => g.toLowerCase());
    const userRoles: UserRole[] = (currentUser.roles || []) as UserRole[];
    const relevantTypes = new Set(userRoles.flatMap(role => ROLE_TYPE_MAP[role] || []));
    const userHomeCity = normalize(currentUser.city || '');

    return opportunities
      .map((opp: Opportunity) => {
        if (opp.author.handle === currentUser.handle || opp.status === 'closed') {
          return { opp, score: -1 };
        }
        if (isFan && !fanAllowedTypes.includes(opp.type)) {
          return { opp, score: -1 };
        }
        // Never show past events in the sidebar widget
        if (isPast(opp)) {
          return { opp, score: -1 };
        }

        const oppCity = normalize(opp.location);
        let score = 0;
        let hasLocationMatch = false;

        // Home city match (+10)
        if (userHomeCity && oppCity === userHomeCity) {
          score += 10;
          hasLocationMatch = true;
        }

        // Travel plan overlap (+15)
        if (currentUser.travelPlans?.length) {
          for (const plan of currentUser.travelPlans) {
            if (!plan.city) continue;
            if (normalize(plan.city) !== oppCity) continue;
            const oppStart = new Date(`${opp.startDate || '1970-01-01'}T00:00:00`);
            const oppEnd = opp.endDate ? new Date(`${opp.endDate}T00:00:00`) : oppStart;
            const planStart = new Date(`${plan.startDate}T00:00:00`);
            const planEnd = new Date(`${plan.endDate}T00:00:00`);
            if (oppStart <= planEnd && oppEnd >= planStart) {
              score += 15;
              hasLocationMatch = true;
              break;
            }
          }
        }

        // Location is required for the sidebar widget
        if (!hasLocationMatch) return { opp, score: 0 };

        // Role match → opportunity type (+8)
        if (relevantTypes.has(opp.type)) score += 8;

        // Instruments (+10) & genres (+5)
        const oppInstruments = (opp.instrument || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        if (oppInstruments.some(i => userInstruments.includes(i))) score += 10;
        const oppGenres = (opp.genre || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        if (oppGenres.some(g => userGenres.includes(g))) score += 5;

        if (opp.salary) score += 3;

        return { opp, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score || new Date(b.opp.timestamp).getTime() - new Date(a.opp.timestamp).getTime())
      .map(item => item.opp)
      .slice(0, 3);
  }, [currentUser, managedProfile, opportunities]);

  return (
    <div className="bg-brand-surface rounded-lg p-4 border border-gray-800 shadow-sm font-urbanist">
      <div className="flex items-center space-x-2 mb-4">
        <SparklesIcon className="h-5 w-5 text-brand-orange" />
        <h3 className="text-lg font-black text-white uppercase tracking-tighter">For You</h3>
      </div>
      {recommendedOpportunities.length > 0 ? (
        <ul className="space-y-3">
          {recommendedOpportunities.map(opp => (
            <li
              key={opp.id}
              className="cursor-pointer group bg-gray-800/20 p-2 rounded-lg border border-transparent hover:border-brand-orange/30 transition-all"
              onClick={() => handleViewOpportunity(opp)}
            >
              <p className="font-bold text-white group-hover:text-brand-orange truncate uppercase text-sm tracking-tight">{opp.title}</p>
              <p className="text-[10px] text-brand-muted truncate font-bold uppercase tracking-widest mt-0.5">{opp.location.split(',')[0]} • {opp.author.name}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-brand-muted italic py-4 text-center">No matches in your current areas.</p>
      )}
      <button
        onClick={() => handleNavigation('recommended')}
        className="w-full text-center text-[10px] font-black text-brand-orange mt-4 hover:underline uppercase tracking-widest pt-2 border-t border-gray-800"
      >
        Expand Explorer
      </button>
    </div>
  );
};
